import React, { useEffect, useState } from "react";
import "./style.css";
import axios from "../../../services/axios";
import {
  IOptionProps,
  IOptionsProps,
} from "../../../@types/reactSelect/IOption";
import { DropdownV2, MultiDropdownV2, CounterInputV2 } from "../../FormControlsV2";
import { sortBy } from "../../../utils/arrays/sortBy";
import { useTaskCreation } from "../../../hooks/useTaskCreation";
import { capitalize } from "../../../utils/strings/capitalize";

interface SprayingProps { }

interface SelectedProductProps extends IOptionProps {
  liter_per_ha?: number;
}

type SelectedProductsProps = Array<SelectedProductProps>;

export const Spraying: React.FC<SprayingProps> = () => {
  const {
    payload,
    setPayload,
    setAllowTaskCreation,
    responsibleId,
    microAreaId,
  } = useTaskCreation();

  const [productOptions, setProductOptions] = useState<IOptionsProps>([]);
  const [applicationTypeOptions, setApplicationTypeOptions] =
    useState<IOptionsProps>([]);
  const [changes, setChanges] = useState<any>();

  const [selectedApplicationType, setSelectedApplicationType] =
    useState<IOptionProps | null>(null);
  const [selectedProducts, setSelectedProducts] =
    useState<SelectedProductsProps>([]);

  const [showProductsChooser, setShowProductsChooser] =
    useState<boolean>(false);
  const [showProductsCounter, setShowProductsCounter] =
    useState<boolean>(false);

  useEffect(() => {
    getProducts();
    getApplicationTypes();
  }, []);

  const getProducts = async () => {
    await axios.get("/tasks/spraying/products").then((response) => {
      if (!response.data) return;
      setProductOptions(
        response.data
          .map((prod: { name: string; id: number }) => ({
            label: prod.name.toLowerCase(),
            value: prod.id,
          }))
          .sort(sortBy("label"))
      );
    });
  };

  const getApplicationTypes = async () => {
    await axios.get("/tasks/spraying/application-types").then((response) => {
      if (!response.data) return;
      setApplicationTypeOptions(
        response.data
          .map((prod: { name: string; id: number }) => ({
            label: prod.name,
            value: prod.id,
          }))
          .sort(sortBy("label"))
      );
    });
  };

  const handleApplicationTypeChoice = (opt: IOptionProps | null) => {
    setSelectedApplicationType(opt);
    const typeId = opt?.value;
    setShowProductsChooser(!!typeId);
    setChanges((prev: any) => ({
      ...prev,
      application_type_id: typeId,
    }));
    if (!typeId) {
      setSelectedProducts([]);
    }
  };

  const handleProductsSelection = (opts: IOptionProps[]) => {
    const next = opts.map((prod) => {
      const prev = selectedProducts.find((s) => s.value === prod.value);
      return prev ? { ...prod, liter_per_ha: prev.liter_per_ha } : { ...prod, liter_per_ha: undefined };
    });
    setSelectedProducts(next);
  };

  const handleProducLiters = (liters: number, index: number) => {
    const currProducts = selectedProducts.slice();
    currProducts[index] = { ...currProducts[index], liter_per_ha: liters };
    setSelectedProducts(currProducts);
  };

  useEffect(() => {
    if (selectedProducts.length === 0) setShowProductsCounter(false);
    else setShowProductsCounter(true);

    if (
      selectedProducts.length === 0 ||
      selectedProducts.some((prod) => typeof prod.liter_per_ha === "undefined")
    )
      return setAllowTaskCreation(false);

    const noZeroCounter = !selectedProducts.some(
      (prod) => prod.liter_per_ha === 0
    );

    setAllowTaskCreation(noZeroCounter);

    if (noZeroCounter) {
      const products = selectedProducts.map((product) => ({
        product_id: product.value,
        liter_per_ha: product.liter_per_ha,
      }));

      setPayload({
        ...payload,
        changes: [
          {
            ...changes,
            assigned_user_id: responsibleId,
            micro_area_id: microAreaId,
            products: products,
          },
        ],
      });
    }
  }, [selectedProducts, changes]);

  return (
    <>
      <DropdownV2
        name="spraying-application-type"
        placeholder="Selecionar tipo de aplicação... "
        label="Tipo de aplicação"
        options={applicationTypeOptions}
        value={selectedApplicationType}
        menuMount="overlay"
        onChange={(opt) => handleApplicationTypeChoice(opt)}
      />
      {showProductsChooser && (
        <>
          <MultiDropdownV2
            name="spraying-products"
            placeholder="Selecionar produto(s) utilizado(s)..."
            label="Produto(s)"
            options={productOptions ?? []}
            value={selectedProducts}
            menuMount="overlay"
            onChange={(opts) => handleProductsSelection(opts)}
          />
          {showProductsCounter &&
            selectedProducts.map((product, index) => {
              return (
                <React.Fragment key={String(product.value)}>
                  <div className="spraying-counter-container">
                    <span className="product-label">
                      {capitalize(product.label.toLowerCase())}
                    </span>
                    <CounterInputV2
                      name={`spraying-ml-${product.value}`}
                      suffix="ml/ha"
                      value={product.liter_per_ha ?? 0}
                      min={0}
                      max={99999999}
                      onChange={(v) => handleProducLiters(v, index)}
                    />
                  </div>
                </React.Fragment>
              );
            })}
        </>
      )}
    </>
  );
};
